import React, { useRef } from 'react';
import { Menu } from 'primereact/menu';
import { useLanguage } from '../../../../contexts/LanguageContext';

// Gerçek küçük resim (thumbnail) API'den gelmiyor (bkz. docs/LmsVideoApi.md §1) — video başına
// sabit, kayıt id'sine göre döngüsel bir gradyan atanıyor, salt görsel çeşitlilik için.
const THUMBNAIL_GRADIENTS = [
    'linear-gradient(135deg, #1E2A5A 0%, #2F3F86 100%)',
    'linear-gradient(135deg, #241A38 0%, #402A5C 100%)',
    'linear-gradient(135deg, #0F2A20 0%, #1E4A38 100%)',
    'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
    'linear-gradient(135deg, #2E1512 0%, #4A241F 100%)',
    'linear-gradient(135deg, #0F2A2E 0%, #1B4D52 100%)'
];

// "Video yüklenmemiş" durumu için çizgili nötr desen (bkz. doc §1 — dosya alanları null).
const NO_VIDEO_PATTERN = 'repeating-linear-gradient(45deg, #E5E7EB 0px, #E5E7EB 10px, #EDEFF2 10px, #EDEFF2 20px)';

const getThumbnailBackground = (video) => {
    if (!video?.fileKey) return NO_VIDEO_PATTERN;
    const idx = Number.isFinite(video?.id) ? video.id % THUMBNAIL_GRADIENTS.length : 0;
    return THUMBNAIL_GRADIENTS[idx];
};

// `feeType` kodları sabit değil (bkz. doc §5) — bu yüzden renklendirme koda göre değil,
// BE'nin döndürdüğü `feeTypeText`in içeriğine göre yapılıyor; tanımadığı bir metinde nötr gri
// kullanır, asla varsayılan bir kodu (1/2) "ücretsiz/ücretli" diye varsaymaz.
const getFeeColor = (feeTypeText) => {
    const normalized = (feeTypeText || '').toLocaleLowerCase('tr-TR');
    if (normalized.includes('ücretsiz') || normalized.includes('free')) return '#16A34A';
    if (normalized.includes('ücretli') || normalized.includes('paid')) return '#D97706';
    return '#6B7280';
};

/**
 * Tek bir video kaydını YouTube tarzı bir kart olarak gösterir. Süre (12:45 gibi) API'de
 * bulunmadığı için gösterilmiyor — bkz. Pass 24 not: uydurma veri gösterilmiyor.
 */
const LmsVideoCard = ({ video, onWatch, onEdit, onDelete, readOnly = false }) => {
    const { th } = useLanguage();
    const menuRef = useRef(null);
    const hasFile = !!video?.fileKey;
    const feeColor = getFeeColor(video?.feeTypeText);

    const menuItems = [
        onEdit && { label: th('common.edit'), icon: 'pi pi-pencil', command: () => onEdit(video) },
        onDelete && { label: th('common.delete'), icon: 'pi pi-trash', command: () => onDelete(video) }
    ].filter(Boolean);

    return (
        <div className="col-12 sm:col-6 lg:col-4">
            <div className="border-round-xl overflow-hidden h-full flex flex-column" style={{ border: '1px solid #EAECF0', backgroundColor: '#FFFFFF', boxShadow: '0 1px 2px rgba(16,24,40,0.04)' }}>
                <div
                    className="flex align-items-center justify-content-center relative"
                    style={{ aspectRatio: '16 / 9', background: getThumbnailBackground(video), cursor: hasFile ? 'pointer' : 'default' }}
                    onClick={() => hasFile && onWatch(video)}
                    title={hasFile ? th('lmsVideo.cardWatch') : th('lmsVideo.noVideoUploaded')}
                >
                    {hasFile ? (
                        <div
                            className="flex align-items-center justify-content-center border-circle"
                            style={{ width: '3.25rem', height: '3.25rem', border: '2px solid rgba(255,255,255,0.85)', backgroundColor: 'rgba(255,255,255,0.12)' }}
                        >
                            <i className="pi pi-play" style={{ fontSize: '1.1rem', color: '#FFFFFF' }} />
                        </div>
                    ) : (
                        <div className="flex flex-column align-items-center gap-2 p-3 border-round-lg" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(16,24,40,0.08)' }}>
                            <i className="pi pi-upload text-color-secondary" style={{ fontSize: '1.1rem' }} />
                            <span className="text-color-secondary text-sm">{th('lmsVideo.noVideoUploaded')}</span>
                        </div>
                    )}
                </div>

                <div className="p-3 flex flex-column gap-1 flex-grow-1">
                    <div className="flex align-items-start justify-content-between gap-2">
                        <span
                            className="font-semibold cursor-pointer white-space-nowrap overflow-hidden text-overflow-ellipsis"
                            style={{ fontSize: '0.95rem' }}
                            onClick={() => hasFile && onWatch(video)}
                        >
                            {video?.tittle}
                        </span>
                        {!readOnly && menuItems.length > 0 && (
                            <>
                                <i className="pi pi-ellipsis-v cursor-pointer text-color-secondary flex-shrink-0" style={{ padding: '0.15rem' }} onClick={(e) => menuRef.current?.toggle(e)} />
                                <Menu model={menuItems} popup ref={menuRef} />
                            </>
                        )}
                    </div>

                    {video?.description && (
                        <span className="text-color-secondary text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">{video.description}</span>
                    )}

                    <div className="flex align-items-center gap-2 mt-2 text-sm">
                        {video?.modelCode && <span className="font-medium">{video.modelCode}</span>}
                        {video?.modelCode && video?.feeTypeText && <span style={{ color: '#D0D5DD' }}>•</span>}
                        {video?.feeTypeText && (
                            <span className="flex align-items-center gap-1" style={{ color: feeColor }}>
                                <span className="border-circle" style={{ width: '0.4rem', height: '0.4rem', backgroundColor: feeColor, display: 'inline-block' }} />
                                {video.feeTypeText}
                            </span>
                        )}
                        <span className="flex align-items-center gap-1 ml-auto text-color-secondary">
                            <i className={video?.isPublic ? 'pi pi-globe' : 'pi pi-lock'} style={{ fontSize: '0.75rem' }} />
                            {th(video?.isPublic ? 'lmsVideo.visibilityPublic' : 'lmsVideo.visibilityPrivate')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LmsVideoCard;
